import React, { useEffect, useState } from 'react';
import { StarRating } from 'components/star-rating';
import { QUESTION_CARD, QUESTION_TITLE } from '../card-classes';

interface Skill {
  key: string;
  label: string;
}

interface QuestionSkillsRatingProps {
  selectedSkills: { [key: string]: number };
  handleRatingChange: (skill: string, rating: number) => void;
  questionText: string;
  questionNumber: number;
  skills: Skill[];
  scaleMax?: number;
  errorMessage?: string;
  containerClassName?: string;
  titleClassName?: string;
  skillsRatingContainerClassName?: string;
  skillRowClassName?: string;
  skillLabelClassName?: string;
  starRatingClassName?: string;
}

export const QuestionSkillsRating: React.FC<QuestionSkillsRatingProps> = ({
  selectedSkills,
  handleRatingChange,
  questionText,
  questionNumber,
  skills,
  scaleMax = 7,
  errorMessage,
  containerClassName,
  titleClassName,
  skillsRatingContainerClassName,
  skillRowClassName,
  skillLabelClassName,
  starRatingClassName,
}) => {
  const [warningSkillKey, setWarningSkillKey] = useState<string | null>(null);

  useEffect(() => {
    if (!warningSkillKey) return;

    const timeout = window.setTimeout(() => setWarningSkillKey(null), 3000);
    return () => window.clearTimeout(timeout);
  }, [warningSkillKey]);

  const onStarChange = (skillKey: string, rating: number) => {
    const nextRatings = {
      ...selectedSkills,
      [skillKey]: rating,
    };

    const allRatedAtMax = skills.length > 1 && skills.every((skill) => nextRatings[skill.key] === scaleMax);

    if (allRatedAtMax) {
      setWarningSkillKey(skillKey);
      return;
    }

    setWarningSkillKey(null);
    handleRatingChange(skillKey, rating);
  };

  return (
    <div className={`${QUESTION_CARD} ${containerClassName || ''}`}>
      <h3 className={`${QUESTION_TITLE} ${titleClassName || ''}`}>
        {questionNumber}. {questionText}
      </h3>
      <div className={`flex flex-col ${skillsRatingContainerClassName || ''}`}>
        {skills.map(skill => (
          <div key={skill.key} className={`flex flex-col items-start gap-2 py-2 md:flex-row md:items-center md:justify-between md:gap-0 ${skillRowClassName || ''}`}>
            <span className={`flex-1 text-sm font-medium text-[#172b4d] ${skillLabelClassName || ''}`}>{skill.label}</span>
            <div className="relative">
              <StarRating
                value={selectedSkills[skill.key]}
                onChange={(rating: number) => onStarChange(skill.key, rating)}
                count={scaleMax}
                startAt={1}
                className={`flex gap-1 self-start ${starRatingClassName || ''}`}
              />
              {warningSkillKey === skill.key && (
                <div className="absolute right-0 top-[calc(100%+8px)] z-[5] w-[min(260px,75vw)] rounded-lg bg-[#101828] px-3 py-2.5 text-xs leading-[1.4] text-white shadow-[0_8px_20px_rgb(16_24_40_/_18%)] before:absolute before:-top-[5px] before:right-3 before:h-2.5 before:w-2.5 before:rotate-45 before:bg-[#101828] before:content-[''] md:w-[260px]" role="alert">
                  Selecting {scaleMax} for every option is not a logical selection. Please vary your ratings.
                </div>
              )}
            </div>
          </div>
        ))}
      </div>
      {errorMessage && <p className="mx-0 mb-0 mt-3 text-sm text-[#E74444]">{errorMessage}</p>}
    </div>
  );
};
